/**
 * Liste over enkeltlokasjoner (spesial-bins utenfor reol-grid) som ikke er
 * plassert i layouten. Lar brukeren velge plasseringstype (gulv/vegg/heng/
 * skap/dock) og plassere lokasjonen på første ledige posisjon i 2D-visningen.
 */

import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { snapToGrid, getRotatedDimensions } from '../utils/positionCalculator';
import { resolveBinRenderInfo } from '../utils/layoutApiMapping';
import type {
  ServerBinPosition,
  ServerSpecialBin,
} from '../../../../../services/warehouseLayoutService';

type PlacementKind = ServerBinPosition['placementKind'];

interface UnplacedBinsListProps {
  /** Søkefilter fra sidepanelet — matcher mot kode og beskrivelse (case-insensitiv) */
  filter?: string;
}

const UnplacedBinsList: React.FC<UnplacedBinsListProps> = ({ filter = '' }) => {
  const { state, dispatch, rackDimensionsMap } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, specialBins, binPositions } = state;

  // Valgt plasseringstype per lokasjon (default FLOOR)
  const [kindByBin, setKindByBin] = useState<Record<string, PlacementKind>>({});

  const specialByCode = useMemo(
    () => new Map(specialBins.map(b => [b.binCode, b])),
    [specialBins]
  );

  // Render-info for allerede plasserte enkeltlokasjoner — brukes til kollisjonssjekk
  const placedBinInfos = useMemo(
    () => binPositions.map(p => resolveBinRenderInfo(p, specialByCode)),
    [binPositions, specialByCode]
  );

  const unplacedBins = useMemo(() => {
    const placed = new Set(binPositions.map(p => p.binCode));
    return specialBins.filter(b => !placed.has(b.binCode));
  }, [specialBins, binPositions]);

  // Søkefiltrert liste (visning)
  const visibleBins = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return unplacedBins;
    return unplacedBins.filter(b =>
      b.binCode.toLowerCase().includes(q) ||
      (b.description1 ?? '').toLowerCase().includes(q)
    );
  }, [unplacedBins, filter]);

  if (!layout || unplacedBins.length === 0) return null;

  /**
   * Enkel ledig-posisjon-skanner: rutenett fra (2,2) i 1m-steg til første
   * posisjon uten kollisjon med plasserte enkeltlokasjoner eller reoler
   * (samme mønster som findOptimalPosition for reoler). Fallback: (2,2).
   */
  const findFreePosition = (widthM: number, depthM: number): { x: number; y: number } => {
    const margin = 0.5;
    const halfW = widthM / 2;
    const halfD = depthM / 2;

    const collides = (x: number, y: number): boolean => {
      for (const bin of placedBinInfos) {
        const rot = (bin.rotation === 90 || bin.rotation === 270)
          ? { width: bin.depthM, depth: bin.widthM }
          : { width: bin.widthM, depth: bin.depthM };
        if (
          Math.abs(x - bin.x) < halfW + rot.width / 2 + margin &&
          Math.abs(y - bin.y) < halfD + rot.depth / 2 + margin
        ) return true;
      }
      for (const rack of layout.racks) {
        const dim = rackDimensionsMap.get(rack.rackNumber);
        if (!dim) continue;
        const rot = getRotatedDimensions(dim, rack.rotation);
        if (
          Math.abs(x - rack.x) < halfW + rot.width / 2 + margin &&
          Math.abs(y - rack.y) < halfD + rot.depth / 2 + margin
        ) return true;
      }
      return false;
    };

    for (let y = 2; y + halfD <= layout.dimensions.width; y += 1) {
      for (let x = 2; x + halfW <= layout.dimensions.length; x += 1) {
        if (!collides(x, y)) {
          return { x: snapToGrid(x, layout.gridUnit), y: snapToGrid(y, layout.gridUnit) };
        }
      }
    }
    // Ingen ledig posisjon funnet — enkel offset-plassering øverst til venstre
    return { x: snapToGrid(2, layout.gridUnit), y: snapToGrid(2, layout.gridUnit) };
  };

  const handlePlaceBin = (bin: ServerSpecialBin) => {
    const kind = kindByBin[bin.binCode] ?? 'FLOOR';
    // Samme mål-oppløsning som renderingen bruker (overrides > StcBinSize > kind-defaults)
    const probe = resolveBinRenderInfo(
      { binCode: bin.binCode, levelNo: 0, placementKind: kind, positionX: 0, positionY: 0, rotation: 0 },
      specialByCode
    );
    const pos = findFreePosition(probe.widthM, probe.depthM);

    dispatch({
      type: 'PLACE_BIN',
      position: {
        binCode: bin.binCode,
        levelNo: 0,
        placementKind: kind,
        positionX: pos.x,
        positionY: pos.y,
        rotation: 0,
      },
    });
  };

  const kindOptions: Array<{ value: PlacementKind; label: string }> = [
    { value: 'FLOOR', label: t('manager.modules.warehouseLayout.unplacedBins.kindFloor', {}, 'Gulv') },
    { value: 'WALL', label: t('manager.modules.warehouseLayout.unplacedBins.kindWall', {}, 'Vegg') },
    { value: 'HANGING', label: t('manager.modules.warehouseLayout.unplacedBins.kindHanging', {}, 'Heng') },
    { value: 'CABINET', label: t('manager.modules.warehouseLayout.unplacedBins.kindCabinet', {}, 'Skap') },
    { value: 'DOCK', label: t('manager.modules.warehouseLayout.unplacedBins.kindDock', {}, 'Dock') },
  ];

  return (
    <div className="px-3 pb-3">
      {visibleBins.length === 0 && (
        <p className="text-xs text-gray-400 italic py-1">
          {t('manager.modules.warehouseLayout.sidebar.noSearchHits', { '0': filter.trim() }, `Ingen treff på «${filter.trim()}»`)}
        </p>
      )}

      <div className="space-y-1">
        {visibleBins.map(bin => (
          <div
            key={bin.binCode}
            className="flex items-center justify-between gap-1.5 p-1.5 rounded-lg text-sm bg-yellow-50 hover:bg-yellow-100"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-medium text-xs">{bin.binCode}</span>
              {bin.description1 && (
                <span className="text-xs text-gray-400 truncate">{bin.description1}</span>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <select
                value={kindByBin[bin.binCode] ?? 'FLOOR'}
                onChange={e =>
                  setKindByBin(prev => ({ ...prev, [bin.binCode]: e.target.value as PlacementKind }))
                }
                className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white"
              >
                {kindOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button
                onClick={() => handlePlaceBin(bin)}
                className="p-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                title={t('manager.modules.warehouseLayout.unplacedBins.place', {}, 'Plasser lokasjon')}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UnplacedBinsList;
